import {
	getBackgroundColorClassName,
	getFontColorClassName,
} from '../../../lib/getColorClassNames';
import {
	MainContentAreaExpandedValue,
	OptimizelyPageContentArea,
} from '../../../types/optimizely';
import { isAccordionBlock } from '../../../types/optimizely/blocks/accordionBlock';
import { isContactsBlock } from '../../../types/optimizely/blocks/contactsBlock';
import { isContentCardBlock } from '../../../types/optimizely/blocks/contentCardBlock';
import { isImageFileBlockData } from '../../../types/optimizely/blocks/imageFile';
import { isPromoBlock } from '../../../types/optimizely/blocks/promoBlock';
import { isQuickLinksBlock } from '../../../types/optimizely/blocks/quickLinksBlock';
import { isSpaceFillerBlock } from '../../../types/optimizely/blocks/spaceFillerBlock';
import { isTextBlockData } from '../../../types/optimizely/blocks/textBlock';
import { isTextImageBlockData } from '../../../types/optimizely/blocks/textImageBlockHP';
import { isTitleBlock } from '../../../types/optimizely/blocks/titleBlock';
import { mapToContactCardsProps } from '../../../utils/contactCards/mapToContactCardsProps';
import { mapToContentCard } from '../../../utils/contentCards/mapToContentCard';
import { mapToPromoCard } from '../../../utils/promoCard/mapToPromoCard';
import { mapToQuickLinksProps } from '../../../utils/quickLinks/mapToQuickLinksProps';
import { mapToTextImageProps } from '../../../utils/textImage/mapToTextImageProps';
import { ImageBlock } from '../molecules/ImageBlock';
import { QuickLinks } from '../molecules/QuickLinks';
import { TextBlock } from '../molecules/TextBlock';
import { TitleBlock } from '../molecules/TitleBlock';
import { AccordionBlock } from './AccordionBlock';
import { ContactCardsBlock } from './ContactCardsBlock';
import { ContentCard } from './ContentCard';
import { PromoBlock } from './PromoBlock';
import { TextAndImageBlock } from './TextAndImageBlock';
import { v4 as uuidv4 } from 'uuid';

type DisplayOptions = {
	displayOption: string;
}[];

interface ContainerProps {
	children: React.ReactElement | (React.ReactElement | null)[] | null;
	bgColor?: string;
	fontColor?: string;
	containerClasses?: string;
}

const getSumOfModulesWidth = (blocks: MainContentAreaExpandedValue[]) => {
	return blocks.reduce((acc, block) => acc + (block.width ?? 0), 0);
};

const isSpaceForNextModule = (
	blocks: MainContentAreaExpandedValue[],
	nextBlock: MainContentAreaExpandedValue,
) => {
	return getSumOfModulesWidth(blocks) + (nextBlock?.width ?? 0) <= 100;
};

const getCorrectDisplayWidth = (displayOptions: string) => {
	return Number(displayOptions.split('percent')[0]);
};

const getColSpanClass = (width?: number) => {
	let classes = 'col-span-24 ';
	if (width === 12.5) return (classes += 'lg:col-span-3');
	if (width === 16.5) return (classes += 'lg:col-span-4');
	if (width === 25) return (classes += 'lg:col-span-6');
	if (width === 33) return (classes += 'lg:col-span-8');
	if (width === 50) return (classes += 'lg:col-span-12');

	return classes;
};

const getBlock = (block: MainContentAreaExpandedValue) => {
	const wrapperClassName = `${getColSpanClass(block.width)} mb-5 lg:mb-0`;

	if (isTitleBlock(block)) {
		const { title, text, style, anchorId } = block;

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<TitleBlock
					title={title.value}
					type={style.value}
					subtitle={text.value}
					anchorId={anchorId}
				/>
			</div>
		);
	}

	if (isTextBlockData(block)) {
		const { text } = block;

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<TextBlock content={text.value} />
			</div>
		);
	}

	if (isImageFileBlockData(block)) {
		const { url, alt } = block;

		if (!url) return null;

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<ImageBlock alt={alt?.value || ''} src={url} />
			</div>
		);
	}

	if (isTextImageBlockData(block)) {
		const props = mapToTextImageProps(block);

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<TextAndImageBlock {...props} />
			</div>
		);
	}

	if (isQuickLinksBlock(block)) {
		const quickLinksProps = mapToQuickLinksProps(block);

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<QuickLinks {...quickLinksProps} />
			</div>
		);
	}

	if (isAccordionBlock(block)) {
		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<AccordionBlock {...block} />
			</div>
		);
	}
	if (isContactsBlock(block)) {
		const contactCardsBlockProps = mapToContactCardsProps(block);

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<ContactCardsBlock {...contactCardsBlockProps} />
			</div>
		);
	}

	if (isContentCardBlock(block)) {
		const contentCardProps = mapToContentCard(block);

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<ContentCard {...contentCardProps} />
			</div>
		);
	}

	if (isPromoBlock(block)) {
		const promoBlockProps = mapToPromoCard(block);

		return (
			<div className={wrapperClassName} key={uuidv4()}>
				<PromoBlock {...promoBlockProps} />
			</div>
		);
	}

	if (isSpaceFillerBlock(block)) {
		return (
			<div
				className={`hidden md:block ${wrapperClassName}`}
				key={uuidv4()}
			></div>
		);
	}

	return null;
};

const Container: React.FC<ContainerProps> = ({
	children,
	bgColor,
	fontColor,
	containerClasses,
}) => {
	const bgColorClass = getBackgroundColorClassName(bgColor ?? 'color-white');
	const fontColorClass = getFontColorClassName(fontColor ?? 'color-black');
	return (
		<div
			className={`grid grid-cols-24 lg:gap-2 xl:gap-y-10 pb-10 ${containerClasses} ${bgColorClass} ${fontColorClass}`}
		>
			{children}
		</div>
	);
};

const moduleMapper = (
	blocks: MainContentAreaExpandedValue[],
	displayOptions: DisplayOptions,
	containerClasses?: string,
) => {
	const mappedModules: any[] = [];
	const currentRow: MainContentAreaExpandedValue[] = [];

	const pushCurrentRow = () => {
		if (currentRow.length > 0) {
			mappedModules.push(
				<Container
					bgColor={currentRow[0].backgroundColor?.value}
					fontColor={currentRow[0].fontColor?.value}
					containerClasses={containerClasses}
					key={uuidv4()}
				>
					{currentRow.map((module) => getBlock(module))}
				</Container>,
			);

			currentRow.length = 0;
		}
	};

	for (const [index, module] of blocks.entries()) {
		module.width = getCorrectDisplayWidth(displayOptions[index].displayOption);

		if (module.width === 100) {
			pushCurrentRow();
			mappedModules.push(
				<Container
					bgColor={module.backgroundColor?.value}
					fontColor={module.fontColor?.value}
					containerClasses={containerClasses}
					key={uuidv4()}
				>
					{getBlock(module)}
				</Container>,
			);
			continue;
		}

		if (isSpaceForNextModule(currentRow, module)) {
			currentRow.push(module);
		} else {
			pushCurrentRow();
			currentRow.push(module);
		}
	}

	pushCurrentRow();
	return mappedModules;
};

export const ContentArea = ({
	contentArea,
	containerClasses,
}: {
	contentArea: OptimizelyPageContentArea;
	containerClasses?: string;
}) => {
	const blocks = contentArea?.expandedValue || [];
	const displayOptions = contentArea?.value || [];

	return (
		<>
			{moduleMapper(blocks, displayOptions as DisplayOptions, containerClasses)}
		</>
	);
};
